import { action, computed, observable } from "mobx";
import _ from "lodash";
import NestStore from "./NestStore";
import Tour from "../models/Tour";
import TourStatistic from "../models/TourStatistic";
import TourRepository from "../repository/TourRepository";

class TourStore extends NestStore {
  /**
   * @property {number|null}
   */
  tourId = null;

  /**
   * @property {Tour}
   * @type {Tour}
   */
  @observable tour;

  /**
   * @property {TourStatistic}
   */
  @observable tourStatistic;

  /**
   * @private
   */
  #tourCopy;

  @computed
  get tourLoaded() {
    return !!this.tour.tourId;
  }

  /**
   * Return if tour has unsaved data or not
   * @returns {boolean}
   */
  @computed
  get hasUnsavedData() {
    return this.tour.isDirty;
  }

  /**
   * @property {Array}
   */
  @observable tours = [];

  /**
   * @type {TourRepository}
   */
  tourRepository = null;

  rootStore = null;

  /**
   * @property {string|null}
   */
  token = null;

  @computed
  get hasSelectedFilters() {
    if (
      this.searchParams.filterValue !== "0" ||
      this.searchParams.keywordString !== "" ||
      (this.searchParams.customerOrCoCustomerId !== "" && this.searchParams.customerOrCoCustomerId !== "0")
    ) {
      return true;
    }

    return false;
  }

  @computed
  get statusFilterDropdownLabel() {
    let dropDownText;
    switch (this.searchParams.filterValue) {
      case "0":
        dropDownText = "clientPanelConstants:filterBy";
        break;
      case "1":
        dropDownText = "clientPanelConstants:photosReady";
        break;
      case "2":
        dropDownText = "clientPanelConstants:inProcess";
        break;
      case "3":
        dropDownText = "clientPanelConstants:inactive";
        break;
      case "4":
        dropDownText = "clientPanelConstants:awaitingPayment";
        break;
      case "5":
        dropDownText = "clientPanelConstants:live";
        break;
      default:
        dropDownText = "clientPanelConstants:filterBy";
    }
    return dropDownText;
  }

  constructor() {
    super();

    this.tour = new Tour();
    this.tourStatistic = new TourStatistic();
    this.tourRepository = new TourRepository();
    this.pagination.itemsPerPage = 16;

    this.setDefaultSearchParams();
  }

  @action
  load(tourId, force = false, modules = null) {
    if (tourId === this.tour.tourId && !force) {
      return Promise.resolve();
    }

    this.isLoading = true;

    return this.getOne(tourId, modules).finally(() => {
      this.isLoading = false;
    });
  }

  @action
  loadStats(tourId, force = false) {
    if (tourId === this.tourStatistic.tourId && !force) {
      return Promise.resolve();
    }
    this.isLoading = true;
    return Promise.all([this.getTourStats(tourId)]).then(() => {
      this.isLoading = false;
    });
  }

  @action
  loadIndex(infinite = false, params) {
    this.isLoading = true;

    return Promise.all([this.getAll(infinite, params)]).finally(() => {
      this.isLoading = false;
    });
  }

  @action
  reset() {
    this.errorMessages = {};
    this.errorMessage = "";
    this.tour = new Tour(this.#tourCopy);
  }

  @action
  save = () => this.saveTour(this.tourId, this.tour.getDirtyData());

  @action
  activateTour = (tourId = null) => this.saveTour(tourId || this.tourId, { isActive: true });

  @action
  deactivateTour = (tourId = null) => this.saveTour(tourId || this.tourId, { isActive: false });

  @action
  changeOrder(order) {
    this.order = order;

    this.pagination.currentPage = 1;

    return this.loadIndex();
  }

  @action
  changePage(page) {
    this.pagination.currentPage = page;
    return this.loadIndex();
  }

  /**
   * Get all.
   */
  @action
  getAll(infinite = false, params = {}) {
    if (infinite) {
      this.pagination.currentPage += 1;
    }

    const paramsRequest = {
      offset: this.offset,
      limit: this.pagination.itemsPerPage,
      orderBy: "creationDts",
      order: this.order,
      ...params,
    };

    return this.tourRepository.fetch(paramsRequest).then(response => {
      this.processEntities(response.data.tours, infinite);
      if (!infinite) {
        this.processPagination(response.data.pagination);
      }
    });
  }

  @action
  processEntities(tours = [], infinite = false) {
    if (!infinite) {
      this.tours = [];
    }

    if (infinite && tours.length === 0) {
      this.infiniteLoadMore = false;
    }

    tours.forEach(toursArray => {
      const tour = new Tour(toursArray);
      this.tours.push(tour);
    });
  }

  @action
  getOne(tourId = null, modules = null) {
    if (tourId) {
      this.tourId = tourId;
    }

    let query = {};

    if (this.token) {
      query.token = this.token;
    }

    if (modules) {
      query.modules = modules;
    }

    if (_.isEmpty(query)) {
      query = null;
    }

    return this.tourRepository
      .getOne(this.tourId, query)
      .then(response => {
        // validate tour access permission.
        if (response.data.tour.deletedDts) {
          throw new Error("Tour not found!");
        }

        this.tour = new Tour(response.data.tour);
      })
      .catch(e => {
        throw e;
      });
  }

  @action
  getTourStats(tourId = null) {
    return this.tourRepository
      .getTourStats(tourId)
      .then(response => {
        if (response.status === "success") {
          this.tourStatistic = new TourStatistic(tourId, response.data.tourStatistics);
        } else {
          this.errorMessage = response.message;
        }
      })
      .catch(e => {
        this.errorMessage = e.message;
        throw e;
      });
  }

  /**
   * Set default search params.
   */
  @action
  setDefaultSearchParams() {
    this.searchParams.publiclyViewable = "";
    this.searchParams.keywordString = "";
    this.searchParams.filterValue = "0";
    this.searchParams.customerId = "0";
    this.searchParams.customerOrCoCustomerId = "0";
  }

  @action
  sendStatsEmail(tourId) {
    // check if we need to save new emails first
    const saveEmails = () => {
      const dirtyData = this.tour.getDirtyData();
      if (dirtyData.statsEmailCcs !== undefined) {
        const { statsEmailCcs } = dirtyData;
        return this.saveTour(tourId, { statsEmailCcs });
      }
      return Promise.resolve();
    };
    return saveEmails().then(() => {
      return this.tourRepository
        .sendStats(tourId)
        .then(res => {
          if (res.status !== "success") {
            throw new Error("Error while sending request");
          }
        })
        .catch(e => {
          this.errorMessage = e.message;
          throw e;
        });
    });
  }

  /**
   * @private
   */
  saveTour(tourId, data, locale = "en") {
    this.isSaving = true;
    this.errorMessages = {};
    this.errorMessage = "";
    return this.tourRepository
      .update(this.tourId, data, locale)
      .then(response => {
        if (response.status === "success" && response.data.tour) {
          this.tour = new Tour(response.data.tour);
          this.#tourCopy = response.data.tour;
        }
        this.isSaving = false;
      })
      .catch(e => {
        this.isSaving = false;
        if (e.status === "error") {
          this.errorMessage = e.message;
        }
        if (e.status === "fail") {
          const keys = Object.keys(e.message);
          const errors = { tour: {} };
          keys.forEach(key => {
            _.set(errors.tour, key, e.message[key]);
          });
          this.errorMessages = errors;
        }
        throw e;
      });
  }

  @action
  delete(tourId) {
    this.isDeleting = true;

    return this.tourRepository
      .delete(tourId)
      .catch(e => {
        this.isDeleting = false;
        this.setErrorMessages(e);
        throw e;
      })
      .finally(() => {
        this.isDeleting = false;
      });
  }

  @action
  setImmersive3dTour(immersive3dTour) {
    this.tour.setImmersive3dTour(immersive3dTour);
  }
}

export default TourStore;
